import { FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  getMaxDateAllowedToReserve,
  getMinDateAllowedToReserve,
  getTimeForReservation
} from "@/lib/utils";
import { RESERVE_TEXTS, SaveReservationData } from "@/lib/utils-reserve";
import { Office, ResourceType } from "@gettactic/api";
import { CalendarIcon } from "@gettactic/components/src/icons/TacticIcons/CalendarIcon";
import { useIsDesignSystem2 } from "@gettactic/hooks";
import { useMemo } from "react";
import { FieldErrors, UseFormReturn } from "react-hook-form";
import { DatePicker } from "../../basic/DatePicker/DatePicker";
import { inputIcon, inputV2Classes } from "../utils/classes";
import { cn } from "@gettactic/helpers";

type ReserveStartDateProps = {
  form: UseFormReturn<any>;
  defaultValue: Date[];
  startTime: Date;
  officeTimeZone: string;
  weekDates: Date[];
  office?: Office;
  resType: ResourceType;
  errors: FieldErrors<SaveReservationData>;
};

export default function ReserveFieldStartDate({
  form,
  defaultValue,
  startTime,
  weekDates,
  officeTimeZone,
  office,
  resType,
  errors
}: ReserveStartDateProps) {
  const minAllowedReserveDate = useMemo(
    () => getMinDateAllowedToReserve(officeTimeZone, weekDates),
    [officeTimeZone, weekDates]
  );
  const maxAllowedReserveDate = useMemo(
    () => getMaxDateAllowedToReserve(officeTimeZone, office, resType),
    [office, officeTimeZone, resType]
  );

  const isV2 = useIsDesignSystem2();
  return (
    <>
      <FormField
        control={form.control}
        name="weekDates"
        render={({ field }) => (
          <FormItem>
            <div className="focus-ring-rainbow flex-1">
              <DatePicker
                minDate={minAllowedReserveDate}
                maxDate={maxAllowedReserveDate ?? undefined}
                onChange={(date: Date | null, event) => {
                  field.onChange(date, event);
                  if (date !== null) {
                    const newTime = getTimeForReservation(
                      date,
                      startTime,
                      office || null
                    );
                    form.setValue("time", newTime);
                    form.setValue("weekDates", [date]);
                    // Update the startTime state to trigger API refresh
                    // This will be handled by the parent component through the form context
                  }
                }}
                selected={field.value[0]}
                className="input w-full rounded-md "
                calendarClassName="tailwind-datepicker shadow-none"
                placeholderText={RESERVE_TEXTS.selectStart}
                variant={isV2 ? "v2" : "default"}
                customInput={
                  <div className="flex w-full flex-1 items-center">
                    <Input
                      variant={isV2 ? "v2" : "default"}
                      readOnly
                      className={cn(
                        "max-h-[42px] flex-1 px-3 pr-10",
                        isV2 && inputV2Classes
                      )}
                      placeholder={RESERVE_TEXTS.selectStart}
                      value={
                        field.value[0] ? field.value[0].toLocaleDateString() : ""
                      }
                    />
                    <div className="absolute right-2.5 flex items-center">
                      <CalendarIcon className={cn(inputIcon)} />
                    </div>
                  </div>
                }
              />
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
      {errors.weekDates ? (
        <p className="text-primary">Please choose a valid date</p>
      ) : null}
    </>
  );
}
